/**
 * Shared host for email task create/link UI inside the layout #taskModal.
 *
 * IMPORTANT: do NOT portal into turbo-frame#task_modal. Hover-prefetch / any
 * Turbo navigation inside that frame replaces it with "Content missing" when
 * the response has no matching frame (CSR content is not Turbo-driven).
 * We mount into a sibling #email-csr-task-modal-root and hide the turbo-frame.
 */

import { Modal } from 'bootstrap'
import { mountTaskModalShell, unmountTaskModalShell } from '../../modules/task_modal_shell.js'

const CSR_ROOT_ID = 'email-csr-task-modal-root'

export function getTaskModalEl() {
  return document.getElementById('taskModal')
}

export function getTaskModalFrame() {
  return document.getElementById('task_modal')
}

export function getOrCreateCsrRoot() {
  const modal = getTaskModalEl()
  const content = modal?.querySelector('.modal-content')
  if (!content) return null

  let root = document.getElementById(CSR_ROOT_ID)
  if (!root) {
    root = document.createElement('div')
    root.id = CSR_ROOT_ID
    root.className = 'email-csr-task-modal-root d-flex flex-column flex-grow-1 min-h-0 w-100'
    root.setAttribute('data-turbo', 'false')
    content.appendChild(root)
  }
  return root
}

export function resetTaskModalLoading() {
  const frame = getTaskModalFrame()
  if (!frame) return
  frame.removeAttribute('src')
  frame.removeAttribute('complete')
  const template = document.getElementById('task-modal-loading-template')
  frame.innerHTML = template?.innerHTML ?? ''
  frame.hidden = false
  frame.style.display = ''
}

function hideTurboFrame(frame) {
  if (!frame) return
  // Drop any pending Turbo Frame navigation / prefetch target.
  frame.removeAttribute('src')
  frame.removeAttribute('complete')
  frame.setAttribute('data-turbo', 'false')
  frame.hidden = true
  frame.style.display = 'none'
  frame.innerHTML = ''
}

function showTurboFrame(frame) {
  if (!frame) return
  frame.hidden = false
  frame.style.display = ''
  frame.removeAttribute('data-turbo')
}

/**
 * Show #taskModal for Preact CSR content.
 * @returns {{ modal: HTMLElement, bs: Modal, shell: object|null, root: HTMLElement, frame: HTMLElement }|null}
 */
export function openTaskModalHost({ flexShell = false, extraClass = 'task-modal--task-form' } = {}) {
  const modal = getTaskModalEl()
  const frame = getTaskModalFrame()
  const root = getOrCreateCsrRoot()
  if (!modal || !frame || !root) return null

  hideTurboFrame(frame)
  root.innerHTML = ''
  root.hidden = false
  root.style.display = ''

  const shell = flexShell
    ? mountTaskModalShell({ modalEl: modal, extraClass })
    : null

  const bs = Modal.getOrCreateInstance(modal)
  bs.show()
  return { modal, bs, shell, root, frame }
}

export function closeTaskModalHost(host) {
  if (!host) return
  if (host.shell) unmountTaskModalShell(host.shell)
  host.bs?.hide()
}

export function teardownTaskModalHost(host) {
  if (!host) return
  if (host.shell) unmountTaskModalShell(host.shell)

  const root = host.root || document.getElementById(CSR_ROOT_ID)
  if (root) {
    root.innerHTML = ''
    root.hidden = true
    root.style.display = 'none'
  }

  showTurboFrame(host.frame || getTaskModalFrame())
  resetTaskModalLoading()
}
